// Resolves the active Git repository from the document store and exposes the
// platform GitApi. The repo is derived from the active document's path (walked
// to its toplevel by the native service) with a fallback to the open workspace
// roots, supporting multiple repos with a switcher. Pure read-side: it only
// resolves roots; mutations live in SourceControlView. All platform.git access
// is guarded because the capability is optional (desktop-only).

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { GitApi, PlatformBridge } from '@slate/core';
import { useDocStore } from '../store/docStore';

export interface GitRepoState {
  /** True only on a platform that exposes the git capability + a GitApi. */
  available: boolean;
  /** The repo the panel currently targets (same as {@link selectedRoot}), or null. */
  root: string | null;
  /** All distinct repo toplevels across the active doc + open workspace roots. */
  repos: string[];
  /** The effective repo (active-doc repo, the single workspace repo, or the user's pick). */
  selectedRoot: string | null;
  /** Switch the active repo when several are open (the panel shows a switcher). */
  selectRoot(root: string): void;
  /** The platform GitApi, or undefined when unavailable. */
  git: GitApi | undefined;
}

/**
 * Resolve the active repository and expose the GitApi. CONFIRMED multi-root
 * rules:
 *  - the active document's repo wins when it resolves to one;
 *  - otherwise a single distinct workspace repo is used with no switcher;
 *  - otherwise the first of several is the default, overridable via the switcher
 *    ({@link GitRepoState.selectRoot}).
 * Repo roots are re-resolved whenever the active doc path or workspace roots
 * change; superseded async runs are dropped. Pass an optional `refreshNonce`
 * (any changing number) to force a re-resolve on demand: a caller that has just
 * `init`ed a repository bumps it so the brand-new repo toplevel resolves and
 * becomes selectable immediately, without waiting for a doc/workspace change.
 */
export function useGitRepo(platform: PlatformBridge, refreshNonce?: number): GitRepoState {
  const git = platform.git;
  const available = platform.capabilities.git === true && git != null;

  const activeId = useDocStore((s) => s.activeId);
  const docs = useDocStore((s) => s.docs);
  const workspaceRoots = useDocStore((s) => s.workspaceRoots);

  const activeDocPath = useMemo(() => {
    const active = docs.find((d) => d.id === activeId);
    return active?.ref?.path ?? null;
  }, [docs, activeId]);

  const rootPaths = useMemo(
    () =>
      workspaceRoots
        .map((r) => r.path)
        .filter((p): p is string => typeof p === 'string' && p.length > 0),
    [workspaceRoots],
  );

  const [activeDocRepo, setActiveDocRepo] = useState<string | null>(null);
  const [repos, setRepos] = useState<string[]>([]);
  const [userSelected, setUserSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!available || !git) {
      setActiveDocRepo(null);
      setRepos([]);
      return;
    }
    let cancelled = false;
    const resolve = async (path: string): Promise<string | null> => {
      try {
        return await git.repoRoot(path);
      } catch {
        return null;
      }
    };
    void (async () => {
      // Index 0 is always the active doc's repo (null when no active doc path);
      // the rest follow the workspace roots in order.
      const [docRepo, ...rootRepos] = await Promise.all([
        activeDocPath ? resolve(activeDocPath) : Promise.resolve<string | null>(null),
        ...rootPaths.map(resolve),
      ]);
      if (cancelled) return;
      const distinct: string[] = [];
      const add = (r: string | null): void => {
        if (r && !distinct.includes(r)) distinct.push(r);
      };
      add(docRepo);
      for (const r of rootRepos) add(r);
      setActiveDocRepo(docRepo);
      setRepos(distinct);
    })();
    return () => {
      cancelled = true;
    };
  }, [available, git, activeDocPath, rootPaths, refreshNonce]);

  const autoRoot = activeDocRepo ?? (repos.length > 0 ? repos[0] : null);
  const selectedRoot = userSelected && repos.includes(userSelected) ? userSelected : autoRoot;

  const selectRoot = useCallback((root: string) => {
    setUserSelected(root);
  }, []);

  return { available, root: selectedRoot, repos, selectedRoot, selectRoot, git };
}
